import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./menu.module.css";
import { useMenu } from "../../../../context/MenuContext";
import { menuData } from "../../../../data/menuData";
import GalerieIcon from "../../../../Components/Assets/Galerie.svg";
import FoodPic from "../../../../Components/Modal/foodPic";

const Menu = ({ onSubcategoryChange }) => {
  const { selectedCategory } = useMenu();
  const currentMenu = menuData[selectedCategory];

  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const prevCategoryRef = useRef(selectedCategory);

  useEffect(() => {
    // Check if category actually changed
    const categoryChanged = prevCategoryRef.current !== selectedCategory;

    if (currentMenu?.subcategories) {
      const firstSubcategory = Object.keys(currentMenu.subcategories)[0];

      // Reset to first subcategory when switching categories OR on initial mount
      if (categoryChanged || selectedSubcategory === null) {
        setSelectedSubcategory(firstSubcategory);
        onSubcategoryChange(firstSubcategory, selectedCategory);
      }
    } else {
      setSelectedSubcategory(null);
      onSubcategoryChange(null, selectedCategory);
    }

    // Update ref to current category
    prevCategoryRef.current = selectedCategory;
  }, [selectedCategory, currentMenu, onSubcategoryChange, selectedSubcategory]);

  // Add debounced resize handler
  const debouncedResizeHandler = useCallback(() => {
    let timeoutId = null;
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 100);
    };
  }, []);

  // Add resize observer effect
  useEffect(() => {
    const resizeHandler = debouncedResizeHandler();
    window.addEventListener("resize", resizeHandler);

    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, [debouncedResizeHandler]);

  if (!currentMenu) return null;

  const handleSubcategoryChange = (key) => {
    setSelectedSubcategory(key);
    onSubcategoryChange(key, selectedCategory);
  };

  const renderMenuItems = (items) => {
    return items.map((item) => {
      const isTasty = item.name === "Tasty";
      return (
        <div
          key={item.id}
          className={`${styles.menuItem} ${isTasty ? styles.tastyItem : ""}`}
        >
          <div className={styles.itemHeader}>
            <h3
              className={`${styles.itemName} ${
                isTasty ? styles.tastyName : ""
              } notranslate`}
              translate="no"
            >
              {item.name}
              {isTasty && <span className={styles.tastyBadge}>★</span>}
            </h3>
            <span className={styles.price}>{item.price}</span>
          </div>
          {item.ingredients && (
            <p className={styles.ingredients}>{item.ingredients}</p>
          )}
        </div>
      );
    });
  };

  const currentSubcategoryItems =
    currentMenu.subcategories?.[selectedSubcategory]?.items;

  return (
    <div className={styles.menuContainer}>
      <div className={styles.headerContainer}>
        <h2 className={styles.categoryTitle}>{currentMenu.category}</h2>
        <img
          src={GalerieIcon}
          alt="Galerie"
          className={styles.galerieIcon}
          onClick={() => setIsModalOpen(true)}
        />
      </div>

      {currentMenu.subcategories && (
        <div className={styles.filterContainer}>
          {["sweet", "savory", "chocolat", "naturel", "other"].includes(
            selectedSubcategory,
          ) &&
            Object.keys(currentMenu.subcategories).length > 1 && (
              <div className={styles.toggleWrapper}>
                {Object.entries(currentMenu.subcategories).map(
                  ([key, subcategory]) => (
                    <button
                      key={key}
                      className={`${styles.toggleButton} ${
                        selectedSubcategory === key ? styles.active : ""
                      }`}
                      onClick={() => handleSubcategoryChange(key)}
                    >
                      {subcategory.title}
                    </button>
                  ),
                )}
              </div>
            )}
        </div>
      )}

      <div
        key={selectedSubcategory ?? selectedCategory}
        className={styles.menuItems}
      >
        {currentMenu.subcategories
          ? currentSubcategoryItems && renderMenuItems(currentSubcategoryItems)
          : renderMenuItems(currentMenu.items)}
      </div>

      <FoodPic
        images={currentMenu.ModalImages}
        title={currentMenu.category}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default Menu;
